import Circle from 'ol/style/Circle';
import DrawInteraction, {createBox} from 'ol/interaction/Draw';
import DropTarget from './drop-target';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import MapboxLayer from './MapboxLayer';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import TileUTFGrid from 'ol/source/UTFGrid';
import debounce from 'lodash/debounce';
import geometryFromExtent from '../util/geometry-from-extent';
import geometryToCoords from '../util/geometry-to-coords';
import setCursor from '../actions/set-cursor';
import setProvenanceData from '../actions/set-provenance-data';
import {Attribution} from 'ol/control';
import {BASE_LAYER_STYLE, LABELS_STYLE, MAPBOX_ACCESS_TOKEN} from '../config';
import {Box} from '@mui/material';
import {DRAG_TYPE} from '../common';
import {Map, TileLayer, VectorLayer} from '@planet/layers';
import {addQueryParams} from '@apps-common/js-client/api/util';
import {connect} from 'react-redux';
import {drawModes, modes, setDrawMode} from '../actions/ui';
import {fromLonLat, transformExtent} from 'ol/proj';
import {getActiveQuad, getSelectedQuad} from '../selectors';
import {hexToRgb, styled} from '@mui/material/styles';
import {load as loadSelectedScene} from '../actions/selected-scene';
import {LIGHT_THEME as planetTheme} from '@apps-common/ui-theme/Themes';
import {
  setByCoordinate as setGeometryByCoordinate,
  setByExtent as setGeometryByExtent,
} from '../actions/geometry';
import {setView as setMapView} from '../actions/map';
import {useAuth} from '@apps-common/auth-react';

const Wrapper = styled('div')({
  flexGrow: 1,
  userSelect: 'none',
  position: 'relative',
});

const Status = props => (
  <Box
    position="absolute"
    left={0}
    bottom={0}
    bgcolor="grey.900"
    padding={0.5}
    borderRadius="4px 4px 0px 0px"
    color="white"
    fontSize="caption.fontSize"
    {...props}
  />
);

const iconColor = hexToRgb(planetTheme.palette.primary.main);
const locationSvg =
  '<svg width="22px" height="32px" viewBox="0 0 22 32" version="1.1" xmlns="http://www.w3.org/2000/svg">' +
  `<path d="M11,0 C4.91857143,0 0,5.00799991 0,11.1999998 C0,19.5999997 11,31.9999995 11,31.9999995 C11,31.9999995 22,19.5999997 22,11.1999998 C22,5.00799991 17.0814286,0 11,0 Z" fill="${iconColor}"></path>` +
  '<path d="M11,15.1999997 C8.792,15.1999997 7,13.4079998 7,11.1999998 C7,8.99199985 8.792,7.19999988 11,7.19999988 C13.208,7.19999988 15,8.99199985 15,11.1999998 C15,13.4079998 13.208,15.1999997 11,15.1999997 Z" fill="white"></path>' +
  '</svg>';

const nullStyle = new Style({});
const pointStyle = new Style({
  image: new Icon({
    anchor: [0.5, 0],
    anchorOrigin: 'bottom-left',
    src: `data:image/svg+xml;utf8,${locationSvg}`,
  }),
});

const cursorStyle = new Style({
  image: new Circle({
    fill: new Fill({
      color: [0, 0, 0],
    }),
    radius: 2,
  }),
});

const getLineStringStyle = color => {
  return new Style({
    stroke: new Stroke({
      color,
      width: 2,
      lineDash: [5, 10],
    }),
  });
};

const getStrokeStyle = (color, dash) => {
  return new Style({
    stroke: new Stroke({
      color,
      lineDash: dash ? [4, 8] : null,
      width: 2,
    }),
  });
};

const getColorForProperties = properties => {
  if (properties.footprint) {
    return planetTheme.palette.primary.main;
  } else if (properties.active) {
    return planetTheme.palette.primary.main;
  } else if (properties.selected) {
    return planetTheme.palette.primary.main;
  }
  return planetTheme.palette.grey[100];
};

const getStyleForGeometry = (geometry, properties) => {
  const color = getColorForProperties(properties);
  switch (geometry.getType()) {
    case 'GeometryCollection':
      return geometry
        .getGeometries()
        .map(subgeometry => getStyleForGeometry(subgeometry, properties));
    case 'Point':
      // TODO: change nullStyle to plain ol' null after issues get resolved
      // context here: https://hello.planet.com/code/apps/mosaics/merge_requests/37#note_48291
      return properties.active ? pointStyle : nullStyle;
    case 'LineString':
      return getLineStringStyle(color);
    case 'Polygon':
      return properties.footprint
        ? getStrokeStyle(color, true)
        : getStrokeStyle(color);
    default:
      return null;
  }
};

const getLayerStyle = feature => {
  return getStyleForGeometry(feature.getGeometry(), feature.getProperties());
};

const augmentGeometry = (geometry, extent) => {
  switch (geometry.type) {
    case 'Point': {
      const crosshairs = geometry.coordinates.map((coord, index) => ({
        type: 'LineString',
        coordinates: index
          ? [
              [extent[0], coord],
              [extent[2], coord],
            ]
          : [
              [coord, extent[1]],
              [coord, extent[3]],
            ],
      }));

      return {
        type: 'GeometryCollection',
        geometries: [geometry, ...crosshairs],
      };
    }
    default:
      return geometry;
  }
};

const ANIMATION_DURATION = 300;
const GEO_PROJECTION = 'EPSG:4326';

const MosaicMap = ({
  loadSelectedScene,
  setCursor,
  setGeometryByExtent,
  setDrawMode,
  ...props
}) => {
  const auth = useAuth();

  const url = useMemo(
    () =>
      props.mosaic
        ? addQueryParams(props.mosaic._links.tiles, {
            token: auth.user?.accessToken,
          })
        : '',
    [auth, props.mosaic]
  );

  const mapRef = useRef(null);
  const drawInteraction = useRef();
  const zoomTimeout = useRef();
  const minProvenanceZoom = useRef(null);
  const attributionRef = useRef();

  const [dragging, setDragging] = useState(false);
  const [utfVisible, setUtfVisible] = useState(false);

  const onProvenanceSourceChange = event => {
    const tileGrid = event.target.getTileGrid();
    if (tileGrid) {
      minProvenanceZoom.current = tileGrid.getMinZoom();
    }
  };

  const provenanceSource = useMemo(() => {
    if (props.mosaic) {
      const source = new TileUTFGrid({
        url: `https://api.planet.com/basemaps/v1/mosaics/${props.mosaic.id}/tiles?token=${auth.user?.accessToken}`,
      });
      source.on('change', onProvenanceSourceChange);
      return source;
    } else {
      return null;
    }
  }, [auth, props.mosaic]);

  const displayData = data => {
    props.setProvenanceData(data);
  };

  const updateProvenanceData = coordinate => {
    const resolution = mapRef.current.getView().getResolution();
    provenanceSource.forDataAtCoordinateAndResolution(
      coordinate,
      resolution,
      debounce(displayData, 300),
      true
    );
  };

  const onClick = event => {
    if (props.mode === modes.SELECTION && props.drawMode === drawModes.POINT) {
      props.setGeometryByCoordinate(event.coordinate);
      updateProvenanceData(fromLonLat(event.coordinate));
    }
  };

  const onDragEnter = event => {
    const types = [...event.dataTransfer.types];
    if (types.includes(DRAG_TYPE)) {
      setDragging(true);
    }
  };

  const onDragLeave = event => {
    const types = [...event.dataTransfer.types];
    if (types.includes(DRAG_TYPE)) {
      setDragging(false);
    }
  };

  const onDragOver = event => {
    event.preventDefault();
  };

  const onDrop = event => {
    if (dragging) {
      setDragging(false);

      const mosaic = event.dataTransfer.getData(DRAG_TYPE);
      props.onDrop(mosaic);
    }
  };

  const onDrawEnd = useCallback(
    event => {
      const view = mapRef.current.getView();
      const extent = event.feature.getGeometry().getExtent();
      const transformedExtent = transformExtent(
        extent,
        view.getProjection(),
        GEO_PROJECTION
      );
      setGeometryByExtent(transformedExtent);
      setDrawMode(drawModes.POINT);
    },
    [setDrawMode, setGeometryByExtent]
  );

  const addDrawInteraction = useCallback(() => {
    drawInteraction.current = new DrawInteraction({
      type: 'Circle',
      freehand: true,
      style: getStrokeStyle('white'),
      geometryFunction: createBox(),
    });
    drawInteraction.current.on('drawend', onDrawEnd);
    mapRef.current.addInteraction(drawInteraction.current);
  }, [onDrawEnd]);

  const removeDrawInteraction = useCallback(() => {
    if (drawInteraction.current) {
      drawInteraction.current.un('drawend', onDrawEnd);
      mapRef.current.removeInteraction(drawInteraction.current);
    }
    drawInteraction.current = null;
  }, [onDrawEnd]);

  const renderGeometry = () => {
    if (!props.geometry || !mapRef.current || props.mode !== modes.SELECTION) {
      return null;
    }

    const view = mapRef.current.getView();
    const extent = view.calculateExtent(mapRef.current.getSize());
    const transformedExtent = transformExtent(
      extent,
      view.getProjection(),
      GEO_PROJECTION
    );

    const features = [
      {
        type: 'Feature',
        geometry: augmentGeometry(props.geometry, transformedExtent),
        properties: {
          active: !props.activeQuad,
        },
      },
    ];

    if (props.selectedQuad) {
      features.push({
        type: 'Feature',
        geometry: geometryFromExtent(props.selectedQuad.bbox),
        properties: {
          selected: true,
        },
      });
    }

    if (props.activeQuad) {
      features.push({
        type: 'Feature',
        geometry: geometryFromExtent(props.activeQuad.bbox),
        properties: {
          active: true,
        },
      });
    }

    if (props.footprint) {
      features.push({
        type: 'Feature',
        geometry: props.footprint,
        properties: {
          footprint: true,
        },
      });
    }

    return (
      <VectorLayer
        data={{
          type: 'FeatureCollection',
          features,
        }}
        layerStyle={getLayerStyle}
      />
    );
  };

  useEffect(() => {
    if (props.drawMode === drawModes.AREA) {
      addDrawInteraction();
    } else {
      removeDrawInteraction();
    }
  }, [props.drawMode, addDrawInteraction, removeDrawInteraction]);

  useEffect(() => {
    if (props.provenanceData) {
      loadSelectedScene();
    }
  }, [loadSelectedScene, props.provenanceData]);

  useEffect(() => {
    if (minProvenanceZoom.current !== null) {
      // work around an open layers UTF bug by delaying visibility of the
      // provenance layer until the map has completed the animated zoom
      // without this, an expensive loop may lock the browser
      // see https://hello.planet.com/jira/browse/WEBAPPS-845?focusedCommentId=581218
      clearTimeout(zoomTimeout.current);
      const utfVisible = props.view.zoom >= minProvenanceZoom.current;
      zoomTimeout.current = setTimeout(() => {
        setUtfVisible(utfVisible);
      }, ANIMATION_DURATION);
    }
  }, [props.view.zoom]);

  useEffect(() => {
    mapRef.current.updateSize();
  }, [props.comparing]);

  useEffect(() => {
    if (mapRef.current) {
      const attributionCtl = new Attribution({
        target: attributionRef.current,
        collapsible: false,
      });

      mapRef.current.addControl(attributionCtl);
    }
  }, []);

  return (
    <Wrapper
      onDragEnter={onDragEnter}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      style={props.style}
      data-qe="mosaic-map"
    >
      <Map
        animation={{duration: ANIMATION_DURATION}}
        innerRef={node => {
          mapRef.current = node;
        }}
        onMapClick={onClick}
        onViewChange={({zoom, center, extent}) =>
          props.setMapView({zoom, center, extent})
        }
        view={props.view}
        style={{
          width: '100%',
          height: '100%',
          cursor: props.mode === modes.SELECTION ? 'crosshair' : 'default',
        }}
      >
        <TileLayer source={provenanceSource} visible={utfVisible} />
        <MapboxLayer
          styleUrl={BASE_LAYER_STYLE}
          accessToken={MAPBOX_ACCESS_TOKEN}
        />
        <TileLayer url={url} visible={!!props.mosaic} zIndex={1} />
        <MapboxLayer
          styleUrl={LABELS_STYLE}
          accessToken={MAPBOX_ACCESS_TOKEN}
          visible={props.labelsVisible}
        />

        {renderGeometry()}
        <VectorLayer
          data={
            props.cursor
              ? {
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: props.cursor,
                  },
                }
              : null
          }
          layerStyle={cursorStyle}
          visible={props.cursorVisible}
        />
      </Map>
      {props.withLabel && props.mosaic && <Status>{props.mosaic.title}</Status>}

      <DropTarget
        open={dragging}
        style={{position: 'absolute'}}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
      />
      {!props.comparing && props.cursor && (
        <Status>
          {geometryToCoords({
            type: 'Point',
            coordinates: props.cursor,
          }).join(', ')}
        </Status>
      )}
      <Box ref={attributionRef} />
    </Wrapper>
  );
};

MosaicMap.propTypes = {
  activeQuad: PropTypes.object,
  comparing: PropTypes.bool.isRequired,
  cursorVisible: PropTypes.bool.isRequired,
  drawMode: PropTypes.string.isRequired,
  footprint: PropTypes.object,
  labelsVisible: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  mosaic: PropTypes.object,
  onDrop: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func.isRequired,
  onMouseLeave: PropTypes.func.isRequired,
  geometry: PropTypes.object,
  style: PropTypes.object,
  view: PropTypes.object.isRequired,
  withLabel: PropTypes.bool,
  provenanceData: PropTypes.string,
  selectedQuad: PropTypes.object,
  cursor: PropTypes.array,
  loadSelectedScene: PropTypes.func,
  setProvenanceData: PropTypes.func,
  setGeometryByCoordinate: PropTypes.func,
  setGeometryByExtent: PropTypes.func,
  setCursor: PropTypes.func,
  setDrawMode: PropTypes.func,
  setMapView: PropTypes.func,
};

const mapStateToProps = state => ({
  selectedQuad: getSelectedQuad(state),
  activeQuad: getActiveQuad(state),
  comparing: state.ui.mode === modes.COMPARE,
  cursor: state.cursor,
  drawMode: state.ui.drawMode,
  footprint: state.footprint,
  geometry: state.geometry.json,
  labelsVisible: state.ui.labels,
  view: {
    center: state.map.center,
    zoom: state.map.zoom,
    extent: state.map.extent,
  },
  mode: state.ui.mode,
  provenanceData: state.provenanceData,
});

const mapDispatchProps = {
  loadSelectedScene,
  setProvenanceData,
  setGeometryByCoordinate,
  setGeometryByExtent,
  setCursor,
  setDrawMode,
  setMapView,
};

export default connect(mapStateToProps, mapDispatchProps)(MosaicMap);
